import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    MenuItem,
    Select,
    Box,
    ButtonBase,
    Tooltip,
    TablePagination,
    Typography,
} from '@mui/material';
import { BulkImportProspectDataDto } from '@zaplify/prospects';
import { InfoIcon, Trash } from 'lucide-react';
import { useEffect, useState } from 'react';

enum MatchType {
    MULTIPLE_MATCHES = 'multiple matches',
    NO_MATCHES = 'no matches',
    SINGLE_MATCHES = 'single matches',
}

const ProspectsTable = ({
    selectedProspects,
    alternatives,
    originalProspects,
    onSelectAlternativeProspect,
    onRemoveUnmatchedProspects,
    page,
    onPageChange,
    perPage,
    onPerPageChange,
}: {
    selectedProspects?: BulkImportProspectDataDto[];
    originalProspects?: BulkImportProspectDataDto[];
    alternatives?: BulkImportProspectDataDto[];
    onSelectAlternativeProspect?: (rownumber: number, alternative: number) => void;
    onRemoveUnmatchedProspects?: () => void;
    page: number;
    onPageChange: (page: number) => void;
    perPage: number;
    onPerPageChange: (perPage: number) => void;
}) => {
    const [tab, setTab] = useState<MatchType>(MatchType.MULTIPLE_MATCHES);
    const [prospectsToDisplay, setProspectsToDisplay] = useState<
        { prospect: BulkImportProspectDataDto; alternatives: BulkImportProspectDataDto[] }[]
    >([]);

    const [multipleMatchesProspects, setMultipleMatchesProspects] = useState<BulkImportProspectDataDto[]>([]);
    const [noMatchesProspects, setNoMatchesProspects] = useState<BulkImportProspectDataDto[]>([]);
    const [singleMatchesProspects, setSingleMatchesProspects] = useState<BulkImportProspectDataDto[]>([]);

    useEffect(() => {
        const multipleMatches = selectedProspects?.filter((prospect) => {
            const matches = alternatives?.filter((alt) => alt.rowNumber === prospect.rowNumber);
            return matches && matches.length > 1;
        });
        setMultipleMatchesProspects(multipleMatches || []);

        const noMatches = selectedProspects?.filter((prospect) => {
            const matches = alternatives?.filter((alt) => alt.rowNumber === prospect.rowNumber);
            return !matches || matches.length === 0;
        });
        setNoMatchesProspects(noMatches || []);

        const singleMatches = selectedProspects?.filter((prospect) => {
            const matches = alternatives?.filter((alt) => alt.rowNumber === prospect.rowNumber);
            return matches && matches.length === 1;
        });
        setSingleMatchesProspects(singleMatches || []);
        console.log('prospectsToDisplayt', prospectsToDisplay);
    }, [selectedProspects, alternatives, originalProspects, alternatives]);

    const handleRemoveUnmatchedProspects = (event) => {
        event.stopPropagation();
        event.preventDefault();
        setTab(MatchType.MULTIPLE_MATCHES);
        onRemoveUnmatchedProspects?.();
    };

    useEffect(() => {
        let prospectsWithAlternatives = multipleMatchesProspects
            .sort((a, b) => a.rowNumber - b.rowNumber)
            .map((selectedProspect) => {
                const alternativeProspects = alternatives?.filter(
                    (alternative) => alternative.rowNumber === selectedProspect.rowNumber
                );
                return {
                    prospect: selectedProspect,
                    alternatives: alternativeProspects,
                };
            });
        switch (tab) {
            case MatchType.MULTIPLE_MATCHES:
                setProspectsToDisplay(prospectsWithAlternatives);
                break;
            case MatchType.NO_MATCHES:
                prospectsWithAlternatives = noMatchesProspects
                    .sort((a, b) => a.rowNumber - b.rowNumber)
                    .map((selectedProspect) => {
                        const alternativeProspects = alternatives?.filter(
                            (alternative) => alternative.rowNumber === selectedProspect.rowNumber
                        );
                        return {
                            prospect: selectedProspect,
                            alternatives: alternativeProspects,
                        };
                    });
                setProspectsToDisplay(prospectsWithAlternatives);
                break;
            case MatchType.SINGLE_MATCHES:
                prospectsWithAlternatives = singleMatchesProspects
                    .sort((a, b) => a.rowNumber - b.rowNumber)
                    .map((selectedProspect) => {
                        const alternativeProspects = alternatives?.filter(
                            (alternative) => alternative.rowNumber === selectedProspect.rowNumber
                        );
                        return {
                            prospect: selectedProspect,
                            alternatives: alternativeProspects,
                        };
                    });
                setProspectsToDisplay(prospectsWithAlternatives);
                break;
        }
    }, [tab, selectedProspects, singleMatchesProspects, noMatchesProspects, multipleMatchesProspects, alternatives]);

    useEffect(() => {
        onPageChange(1);
    }, [tab]);

    const headCells = [
        { id: 'fullName', label: 'Name' },
        { id: 'organizationName', label: 'Organization Name' },
        { id: 'email', label: 'Email' },
        { id: 'location', label: 'Location' },
        { id: 'occupationTitle', label: 'Occupation Title' },
        { id: 'organizationDomain', label: 'Organization Domain' },
        { id: 'linkedinProfileUrl', label: 'LinkedIn Profile Url' },
    ];

    const filteredHeadCells = headCells.filter((headCell) =>
        selectedProspects?.some((prospect) => prospect[headCell.id])
    );

    const handleChangePage = (event, newPage) => {
        onPageChange(newPage + 1);
    };

    const handleChangeRowsPerPage = (event) => {
        onPerPageChange(event.target.value);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                alignItems: 'center',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '16px',
                    '& button': {
                        fontFamily:
                            '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu","Cantarell", "Fira Sans", "Droid Sans", sans-serif !important',
                        display: 'flex',
                        height: '32px',
                        padding: '4px 0px',
                        alignItems: 'center',
                        gap: 'var(--S-s-regular, 8px)',
                        borderBottom: '2px solid transparent',
                        fontWeight: 500,
                        fontSize: '15px',
                        transition: '200ms',
                        '&.active': {
                            borderBottom: '2px solid var(--grey-palette-grey-6, #3A4B59)',
                            color: '#3A4B59',
                        },
                        '&.inactive': {
                            color: 'var(--grey-palette-grey-4, #969FAC)',
                            '&:hover': {
                                color: '#3A4B59',
                            },
                        },
                    },
                }}
            >
                <ButtonBase
                    className={tab === MatchType.MULTIPLE_MATCHES ? 'active' : 'inactive'}
                    onClick={() => setTab(MatchType.MULTIPLE_MATCHES)}
                >
                    Multiple matches
                    <Box
                        sx={{
                            display: 'flex',
                            padding: 'var(--spacing-2, 2px) var(--spacing-3, 6px)',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 'var(--spacing-0, 0px)',
                            borderRadius: 'var(--radius-full, 360px)',
                            background: 'var(--background-badge-gray, #E9EAEC)',
                            color: 'var(--text-base-secondary, rgba(15, 19, 36, 0.60))',
                        }}
                    >
                        {multipleMatchesProspects?.length || 0}
                    </Box>
                </ButtonBase>
                <ButtonBase
                    className={tab === MatchType.NO_MATCHES ? 'active' : 'inactive'}
                    onClick={() => setTab(MatchType.NO_MATCHES)}
                >
                    No matches
                    <Box
                        sx={{
                            display: 'flex',
                            padding: 'var(--spacing-2, 2px) var(--spacing-3, 6px)',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 'var(--spacing-0, 0px)',
                            borderRadius: 'var(--radius-full, 360px)',
                            background: 'var(--background-badge-gray, #E9EAEC)',
                            color: 'var(--text-base-secondary, rgba(15, 19, 36, 0.60))',
                        }}
                    >
                        {noMatchesProspects?.length || 0}
                    </Box>
                    <Tooltip title={noMatchesProspects.length > 0 ? 'Delete' : ''}>
                        <ButtonBase onClick={handleRemoveUnmatchedProspects}>
                            <Trash size={16} />
                        </ButtonBase>
                    </Tooltip>
                </ButtonBase>
                <ButtonBase
                    className={tab === MatchType.SINGLE_MATCHES ? 'active' : 'inactive'}
                    onClick={() => setTab(MatchType.SINGLE_MATCHES)}
                >
                    Single matches
                    <Box
                        sx={{
                            display: 'flex',
                            padding: 'var(--spacing-2, 2px) var(--spacing-3, 6px)',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 'var(--spacing-0, 0px)',
                            borderRadius: 'var(--radius-full, 360px)',
                            background: 'var(--background-badge-gray, #E9EAEC)',
                            color: 'var(--text-base-secondary, rgba(15, 19, 36, 0.60))',
                        }}
                    >
                        {singleMatchesProspects?.length || 0}
                    </Box>
                </ButtonBase>
                <Tooltip
                    title={`We've matched your CSV against the database of prospects. Select the data you would like to use for each person with "alternatives".`}
                >
                    <InfoIcon size={16} />
                </Tooltip>
            </Box>
            <TableContainer
                component={Paper}
                sx={{
                    border: '1px solid #E4E6EB',
                    boxShadow: '0px 2px 6px rgba(112, 144, 176, 0.07)',
                    width: '90%',
                    minWidth: '90%',
                    marginBottom: '40px',
                    maxHeight: '400px', // Set max height for scrollable table
                    overflowY: 'auto', // Enable vertical scrolling
                }}
            >
                <Table aria-label="simple table" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {filteredHeadCells.map((headCell) => (
                                <TableCell key={headCell.id}>{headCell.label}</TableCell>
                            ))}
                            {tab !== MatchType.NO_MATCHES && <TableCell>Alternatives</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {prospectsToDisplay &&
                            prospectsToDisplay
                                .slice((page - 1) * perPage, page * perPage)
                                .map((prospectWithAlternatives, index) => {
                                    const { prospect, alternatives } = prospectWithAlternatives;

                                    return (
                                        <TableRow key={index}>
                                            {filteredHeadCells.map((headCell) => (
                                                <TableCell key={headCell.id}>{prospect[headCell.id]}</TableCell>
                                            ))}
                                            {alternatives.length > 0 && (
                                                <TableCell align="right">
                                                    <Select
                                                        value={
                                                            originalProspects?.includes(prospect)
                                                                ? -1
                                                                : alternatives.indexOf(prospect)
                                                        }
                                                        onChange={(e) => {
                                                            onSelectAlternativeProspect?.(
                                                                prospect.rowNumber,
                                                                e.target.value as number
                                                            );
                                                        }}
                                                        style={{ width: 200, textAlign: 'center' }} // Fix width and align center
                                                    >
                                                        <MenuItem value={-1}>Original</MenuItem>
                                                        {alternatives.map((alternative, altIndex) => (
                                                            <MenuItem key={altIndex} value={altIndex}>
                                                                {`${alternative.fullName} at ${alternative.organizationName}`}
                                                                <br />
                                                                {alternative.location}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                    </TableBody>
                </Table>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'white',
                        padding: '8px 16px',
                    }}
                >
                    <Typography variant="body2">
                        Page {page} of {Math.ceil(prospectsToDisplay.length / perPage)}
                    </Typography>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 75, 100]}
                        component="div"
                        count={prospectsToDisplay.length}
                        rowsPerPage={perPage}
                        page={page - 1}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            </TableContainer>
        </Box>
    );
};

export default ProspectsTable;
